/**
 * RecommendationsSection — Iter-38 Stage 2 (Action Plan).
 *
 * Deliberately COMPLEMENTARY to the Summary Card, not duplicative:
 *
 *   Summary Card          → executive overview (score + tally + top-3)
 *   Action Plan (this)    → the COMPLETE prioritized checklist, every
 *                            recommendation, difficulty on every row —
 *                            designed to actually work through fixes
 *
 * The list is ordered by severity (High → Medium → Recommended) and
 * numbered across the whole checklist so the customer can literally
 * work top-to-bottom.
 */
import { Lightbulb } from "lucide-react";
import { bucketIssues, actionHeadlineFor } from "./ReportSummaryCard";

const DIFF_PIN_CLS = {
  easy:      "text-emerald-700 border border-emerald-300",
  moderate:  "text-charcoal-600 border border-charcoal-300",
  developer: "text-charcoal-700 border border-charcoal-500",
};

function DifficultyPin({ diff, label, testId }) {
  if (!label) return null;
  const cls = DIFF_PIN_CLS[diff] || DIFF_PIN_CLS.moderate;
  return (
    <span
      className={`inline-block bg-white text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded whitespace-nowrap ${cls}`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}

export default function RecommendationsSection({ report }) {
  const { buckets } = bucketIssues(report);
  // Complete prioritized checklist — every amber/red check in severity
  // order. Numbered continuously so it reads as a single top-to-bottom plan.
  const allIssues = [...buckets.high, ...buckets.medium, ...buckets.recommended];
  const aiExtras = Array.isArray(report?.recommendations) ? report.recommendations : [];

  if (!allIssues.length && !aiExtras.length) return null;

  return (
    <div
      className="mb-5 rounded-xl bg-amber-50/70 border border-amber-100 p-5"
      data-testid="recommendations-section"
    >
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb className="w-4 h-4 text-amber-600" />
        <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-amber-700">
          Your Action Plan
        </p>
      </div>
      <p className="text-[12px] text-charcoal-600 mb-3">
        Everything Mission Watch found this scan, ordered by priority. Work
        your way down — each fix takes you a step closer to a healthier site.
      </p>

      {allIssues.length > 0 && (
        <ol className="space-y-2" data-testid="recs-full-list">
          {allIssues.map((c, i) => (
            <li
              key={c.key || `i-${i}`}
              className="flex items-start gap-2.5 text-sm text-charcoal-800 leading-snug"
              data-testid={`recs-item-${i}`}
            >
              <span className="mt-0.5 text-[12px] font-mono text-charcoal-500 shrink-0 w-6">
                {i + 1}.
              </span>
              <a
                href={`#issue-${c.key}`}
                className="flex-1 hover:underline decoration-charcoal-400"
              >
                {actionHeadlineFor(c)}
              </a>
              <DifficultyPin
                diff={c.fix_difficulty}
                label={c.fix_difficulty_label}
                testId={`recs-difficulty-${i}`}
              />
            </li>
          ))}
        </ol>
      )}

      {aiExtras.length > 0 && (
        <div className="mt-4 pt-3 border-t border-amber-200/50" data-testid="recs-ai-extras">
          <p className="text-[10px] font-mono uppercase tracking-[0.14em] text-charcoal-500 mb-2">
            Additional Notes from Mission Watch
          </p>
          <ul className="space-y-1.5">
            {aiExtras.map((rec, i) => (
              <li
                key={`ai-${i}`}
                className="flex items-start gap-2.5 text-sm text-charcoal-700 leading-snug"
                data-testid={`recs-ai-item-${i}`}
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-charcoal-300 shrink-0" />
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
